import React, { ReactElement, useCallback, useState } from "react";
import { ErrorDialog, IErrorDialogData } from "../../components/ErrorDialog";
import { formatPotentialContractError } from "../../utils/errors";

interface IErrorDialogContextType {
  showError: (
    header: string,
    message: string,
    action?: IErrorDialogData["action"]
  ) => void;
  hideError: () => void;
}

interface IErrorDialogContextProviderProps {
  children: ReactElement;
}

/**
 * The default is a no-op, so `showError` outside the provider silently discards
 * the dialog. That is exactly the failure shape that hid the Switch Network
 * dialog: nothing rendered, nothing logged, and the code looked correct.
 *
 * A warning, not a throw — throwing would break tests that render components in
 * isolation. Production behaviour is unchanged.
 */
const missingProvider = (what: string) => (header?: string) => {
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.error(
      `[ErrorDialogContext] ${what}(${header ?? ""}) was called with no ` +
        `ErrorDialogContextProvider above it. The dialog will not appear. ` +
        `Mount ErrorDialogContextProvider (see App.tsx).`
    );
  }
};

export const ErrorDialogContext = React.createContext<IErrorDialogContextType>({
  showError: missingProvider("showError"),
  hideError: missingProvider("hideError"),
});

export function ErrorDialogContextProvider({
  children,
}: IErrorDialogContextProviderProps) {
  const [error, setError] = useState<IErrorDialogData | null>(null);

  const showError = useCallback(
    (
      header: string,
      message: string,
      action?: IErrorDialogData["action"]
    ) => {
      setError({
        header,
        message: formatPotentialContractError(message) || message,
        action,
      });
    },
    []
  );

  const hideError = useCallback(() => {
    setError(null);
  }, []);

  return (
    <ErrorDialogContext.Provider value={{ showError, hideError }}>
      {children}
      <ErrorDialog error={error} onClose={hideError} />
    </ErrorDialogContext.Provider>
  );
}
